import classnames from 'classnames';
import PropTypes from 'prop-types';
import React from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { pushParams, resetParams } from 'dpl/actions/query_params';
import Icon from 'dpl/common/components/Icon';
import {
  FILTER_DISTANCE_IN_MILES,
  FILTER_LATITUDE,
  FILTER_LOCATION_NAME,
  FILTER_LONGITUDE,
  FILTER_POSTAL_CODE,
  LOCATION_REDIRECT,
  PAGE,
  SORT
} from 'dpl/constants/query_params';
import {
  RESULTS_TYPES,
  SORT_NEAR_YOU
} from 'dpl/puppy_search_results/utils/constants';
import useSearchFiltering from 'dpl/search_result_filters/hooks/useSearchFiltering';
import useSearchLocation from 'dpl/search_result_filters/hooks/useSearchLocation';
import useToggle from 'dpl/shared/hooks/useToggle';
import { isLessThanBreakpoint } from 'dpl/util/grid';
import { addToQuery, removeFromQuery } from 'dpl/util/queryString';

import LocationSelectionModal from './LocationSelectionModal';

const IS_LT_MD = isLessThanBreakpoint('md');
const PIN_ICON_SIZE = IS_LT_MD ? '20px' : '24px';

/** @param {any} props */
export default function UserLocation({ className = null }) {
  const dispatch = useDispatch();
  const { resultsType } = useSearchFiltering();
  const { preappliedFilters, rootPath } = useSelector(({ server }) => server);
  const { locationName } = useSearchLocation();
  const [isModalOpen, toggleIsModalOpen] = useToggle(false);

  async function handleLocationSelect(location) {
    const params = {
      ...(location && { [FILTER_LOCATION_NAME]: location.name }),
      // We're only passing along distance_in_miles for analytics purposes
      // since postal codes are appended automatically when ip_location_filter
      // so this is the only reliable way to know if location filtering is being done
      [FILTER_DISTANCE_IN_MILES]: '0',
      ...(resultsType === RESULTS_TYPES.BREEDERS && { [SORT]: SORT_NEAR_YOU })
    };

    if (preappliedFilters?.stateSlug) {
      const newSearchStr = removeFromQuery(PAGE, window.location.search);

      window.location.href = `${rootPath}/?${LOCATION_REDIRECT}=true&${newSearchStr}#${addToQuery(
        params,
        window.location.hash
      )}`;
    } else {
      dispatch(
        resetParams(
          FILTER_LATITUDE,
          FILTER_LONGITUDE,
          FILTER_LOCATION_NAME,
          FILTER_POSTAL_CODE,
          { hash: true }
        )
      );

      dispatch(pushParams(params, { hash: true }));
    }
  }

  return (
    <>
      <div
        className={classnames(
          'UserLocation flex items-center gap-4',
          className
        )}
      >
        <Icon
          className="tertiary"
          name="fetch-pin"
          height={PIN_ICON_SIZE}
          width={PIN_ICON_SIZE}
        />
        <div className="flex gap-6">
          <span>Your location</span>
          <button
            type="button"
            className="fw-medium underline"
            onClick={toggleIsModalOpen}
          >
            {locationName || 'Set location'}
          </button>
        </div>
      </div>
      {isModalOpen && (
        <LocationSelectionModal
          locationName={locationName}
          onSelect={handleLocationSelect}
          onClose={toggleIsModalOpen}
        />
      )}
    </>
  );
}

UserLocation.propTypes = {
  className: PropTypes.string
};
